import { Injectable } from '@angular/core';
import { HttpClient, HttpRequest } from '@angular/common/http';
import * as jwt_decode from "jwt-decode";
import { NavigationExtras, Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { Application } from 'src/model/Application';
import { CookieService } from 'ngx-cookie-service';
import { DashboardWidgetType } from '../customer/Models/dashboardWidgets';
import { AnalyticsService } from './analytics.service';

class TokenClaims {
  user: number;
  email: string;
  useScanner: boolean;
  dashboards: Array<string>;
  permissions: string[];
  logoId = "0";
  logoPath = "";
  reportPath= "";

  constructor() {
    this.user = 0;
    this.email = '';
    this.useScanner = false;
    this.permissions = [];
  }
}

@Injectable({
  providedIn: 'root'
})


export class LoginService {

  tokenValues: any = {};
  tokenClaims: TokenClaims;
  isImpersonating: boolean = false;

  private applications: Application[];

  constructor(
    private http: HttpClient,
    private router: Router,
    private cookieService: CookieService,
    private analytics: AnalyticsService
  ) { }

  async processLogin(email, password) {
    const user = { username: email, password: password };
    const api = `${environment.url}/login`;

    const result = await this.http.post<any>(api, user).toPromise();
    localStorage["token"] = result.token;
    await this.validateToken();
    return result.token;
  }

  async impersonateUser(userId: number, reason: string) {
    const api = `${environment.url}/user/${userId}/impersonate`;

    const result = await this.http.post<string>(api, {reason}).toPromise();
    localStorage["token"] = result;
    localStorage["isImpersonating"] = true;
    await this.validateToken();
    return result;
  }


  async validateToken(redirectToLogin: boolean = true) {
    const token = await this.isLoggedIn();
    if (token) {
      try {
        this.isImpersonating = this.checkImpersonation();
        this.tokenValues = jwt_decode.jwtDecode(token);
      } catch (error) {
        // Bad/stale token in storage can crash app bootstrap; clear and force re-auth.
        localStorage.removeItem("token");
        localStorage.removeItem("isImpersonating");
        if (redirectToLogin) {
          this.logout();
        }
        return;
      }
      this.tokenClaims = new TokenClaims();
      this.tokenClaims.email = this.tokenValues.email;
      this.tokenClaims.user = this.tokenValues.user;
      this.tokenClaims.useScanner = this.tokenValues.useScanner;
      this.tokenClaims.dashboards = this.tokenValues.dashboards ? JSON.parse(this.tokenValues.dashboards) : [];
      this.tokenClaims.permissions = this.parsePermissions(this.tokenValues.permissions);
      this.tokenClaims.logoId = this.tokenValues.logoId ? this.tokenValues.logoId : "0";
      this.tokenClaims.logoPath = this.tokenValues.logoPath ? this.tokenValues.logoPath : "";
      this.tokenClaims.reportPath = this.tokenValues.reportPath ? this.tokenValues.reportPath : "";
      await this.getUserApplications();
      this.syncAnalyticsIdentity();
    } else {
      if (redirectToLogin) {
        this.logout();
      }
    }
  }

  async isLoggedIn(): Promise<string> {
    const token = localStorage.getItem("token");
    if (token) {
      return token;
    } else {
      return this.checkForCookieLogin();
    }
  }

  private checkImpersonation() {
    return localStorage['isImpersonating'] === 'true';
  }

  getToken(): string {
    const token = localStorage.getItem("token");
    return token;
  }

  getPermissions(): string[] {
    return this.tokenClaims?.permissions ?? [];
  }

  hasPermission(permissionName: string): boolean {
    const normalizedPermission = this.normalizePermission(permissionName);
    if (!normalizedPermission) {
      return false;
    }

    return this.getPermissions().includes(normalizedPermission);
  }

  async getUserApplications(): Promise<Application[]> {
    if (this.getToken()) {
      if (!this.applications) {
        try {
          this.applications = await this.http.get<Application[]>(`${environment.url}/user/applications`).toPromise();
          this.applyApplicationRouteOverrides();
        } catch (err) {
          if (err.status === 401) {
            this.logout();
            return;
          }
          this.applications = [];
        }
      }
      return this.applications;
    }
  }

  // Route the legacy `fmx-map` application to the new Google-map based view.
  // Done here (rather than in HomeComponent) so the override is applied
  // consistently for AuthGuard checks on direct navigation / page refresh.
  private applyApplicationRouteOverrides() {
    const fmxMapIndex = this.applications?.findIndex(
      app => app.applicationName === 'fmx-map'
    );
    if (fmxMapIndex !== undefined && fmxMapIndex >= 0) {
      this.applications[fmxMapIndex].route = '/customer/fmx-google-map';
    }
  }

  private parsePermissions(permissionsClaim: unknown): string[] {
    if (!permissionsClaim) {
      return [];
    }

    let values: string[] = [];
    if (typeof permissionsClaim === 'string') {
      values = permissionsClaim.split(',');
    } else if (Array.isArray(permissionsClaim)) {
      values = permissionsClaim.filter((item) => typeof item === 'string');
    }

    const unique = new Set(values.map((value) => this.normalizePermission(value)).filter(Boolean));
    return Array.from(unique);
  }

  private normalizePermission(value: string): string {
    return (value ?? '').trim().toLowerCase();
  }

  private async checkForCookieLogin(): Promise<string> {
    const cookie = this.cookieService.get('mlapAuth');
    if (cookie) {
      localStorage['token'] = cookie;
      this.cookieService.delete('mlapAuth', undefined, undefined, undefined, "Lax");
      return cookie;
    }
    return undefined;

  }

  logout(navigationExtras?: NavigationExtras) {
    this.applications = undefined;
    localStorage.clear();
    this.tokenClaims = new TokenClaims();
    this.isImpersonating = false;
    this.analytics.setImpersonating(false);
    this.analytics.reset();
    this.router.navigate(['/login'], navigationExtras);
  }

  async forgotPassword(username: string) {
    const api = `${environment.url}/forgotPassword`;
    await this.http.post<any>(api, {username: username}).toPromise();
  }

  async validatePasswordToken(token: string) {
    return await this.http.post<any>(`${environment.url}/passwordTokenValidate`, {token: token}).toPromise();
  }

  async resetPassword(token: string, password: string) {
    const data = {token: token, password: password};
    await this.http.post<any>(`${environment.url}/setPassword`, data).toPromise();
  }

  async getDashboardConfig(): Promise<DashboardWidgetType[] | undefined> {
    const config = await this.http.get<DashboardWidgetType[] | undefined>(`${environment.connectAPI}/user/icecloud-dashboard-config`).toPromise();
    return config ?? undefined;
  }

  async saveDashboardConfig(config: DashboardWidgetType[]) {
    await this.http.put(`${environment.connectAPI}/user/icecloud-dashboard-config`, config).toPromise();
  }

  private syncAnalyticsIdentity(): void {
    if (this.isImpersonating) {
      this.analytics.setImpersonating(true);
      return;
    }

    this.analytics.setImpersonating(false);
    const userId = this.tokenClaims?.user;
    if (!userId) {
      return;
    }

    const apps = this.applications ?? [];
    this.analytics.identify(String(userId), {
      applications: apps.map(app => app.applicationName).filter(Boolean).join(',').slice(0, 36),
      has_customer: apps.some(app => (app.route ?? '').includes('/customer')) ? 'true' : 'false',
      has_admin: apps.some(app => (app.route ?? '').includes('/admin')) ? 'true' : 'false',
      has_principal: apps.some(app => (app.route ?? '').includes('/principal')) ? 'true' : 'false'
    });
  }

}
